/**
 * BST Blog — horizontal scroll + pagination on mobile/tablet.
 */
const BST_BLOG_SELECTORS = {
  card: '[data-bst-blog-card]',
  track: '[data-bst-blog-track]',
  dot: '[data-bst-blog-dot]',
};

const BST_BLOG_DESKTOP_MQ = '(min-width: 990px)';

class BstBlogComponent extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.dots = [];
    this.track = null;
    this.activeIndex = 0;
    this.desktopMq = null;
    this.isDesktop = false;

    this.onDotClick = this.onDotClick.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.cards = [...this.querySelectorAll(BST_BLOG_SELECTORS.card)];
    this.dots = [...this.querySelectorAll(BST_BLOG_SELECTORS.dot)];
    this.track = this.querySelector(BST_BLOG_SELECTORS.track);

    if (!this.cards.length) return;

    this.desktopMq = window.matchMedia(BST_BLOG_DESKTOP_MQ);
    this.isDesktop = this.desktopMq.matches;
    this.setActive(0, { scroll: false, immediate: true });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.onDotClick);
    });

    if (this.track) {
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeyDown);
    }

    this.desktopMq.addEventListener('change', this.onMediaChange);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  disconnectedCallback() {
    if (this.desktopMq) {
      this.desktopMq.removeEventListener('change', this.onMediaChange);
    }
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeyDown);
    }
  }

  onSectionLoad(event) {
    if (!event.target.contains(this)) return;
    this.setActive(0, { scroll: !this.isDesktop });
  }

  onMediaChange(event) {
    this.isDesktop = event.matches;
    this.setActive(this.activeIndex, { scroll: false });
  }

  onDotClick(event) {
    const index = parseInt(event.currentTarget.dataset.bstBlogDot, 10);
    if (Number.isNaN(index)) return;
    this.setActive(index, { scroll: true });
  }

  onKeyDown(event) {
    if (this.isDesktop || this.cards.length < 2) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.setActive(Math.min(this.activeIndex + 1, this.cards.length - 1), { scroll: true });
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.setActive(Math.max(this.activeIndex - 1, 0), { scroll: true });
    }
  }

  onScroll() {
    if (this.isDesktop || !this.track || this._scrollRaf) return;

    this._scrollRaf = window.requestAnimationFrame(() => {
      this._scrollRaf = null;
      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;

      let closest = this.activeIndex;
      let closestDist = Infinity;

      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const dist = Math.abs(cardCenter - trackCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closest = index;
        }
      });

      if (closest !== this.activeIndex) {
        this.setActive(closest, { scroll: false });
      }
    });
  }

  setActive(index, options = {}) {
    const { scroll = false, immediate = false } = options;
    if (index < 0 || index >= this.cards.length) return;

    this.activeIndex = index;

    this.cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === index);
    });

    this.dots.forEach((dot) => {
      const dotIndex = parseInt(dot.dataset.bstBlogDot, 10);
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (scroll && this.track && !this.isDesktop) {
      const card = this.cards[index];
      const behavior = immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
      const offset = card.offsetLeft - this.track.offsetLeft;

      this.track.scrollTo({ left: Math.max(0, offset), behavior });
    }
  }
}

if (!customElements.get('bst-blog-component')) {
  customElements.define('bst-blog-component', BstBlogComponent);
}
